import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { EarlyDisbursementTag } from "@/components/early-disbursement-tag";
import { OverdueTag } from "@/components/overdue-tag";
import { RetryPayoutModal } from "@/components/retry-payout-modal";
import { VoidPayoutModal } from "@/components/void-payout-modal";
import { PayNowModal, type PayNowPayoutNotPaid } from "@/components/pay-now-modal";
import { canBePaidNow, getPayoutsNotPaidNow } from "@/lib/pay-now";
import { getErrorMessage } from "@/lib/axios";
import { getPayoutSourceLabelLines, getSourceLabelLines, PayoutSourceTags } from "@/components/payout-source";
import { getPayoutMemoTexts, PayoutMemoLines } from "@/components/payout-memos";
import { getPaymentFailureCopy } from "@/lib/payment-failure-copy";
import { isTerminalFailure } from "@/lib/payout-retry-status";
import { useOrganizationTimeZone } from "@/hooks/useOrganizationTimeZone";
import { formatOrganizationDate } from "@/lib/organization-date";
import { formatRelease } from "@/lib/payout-release";
import { formatCurrency, formatPercent, getStatusBadgeStyles } from "@/lib/payout-utils";
import {
	getGetPayableByIdQueryOptions,
	useGetPayableById,
	usePayPayoutNow,
} from "@fp/admin-api-client/payouts/payouts.client";
import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import { useGetApiAdminFundingBalance } from "@fp/admin-api-client/funding/funding.client";
import {
	DataRefreshEvent,
	DisbursementTiming,
	type PartyType,
	PayoutRequestWorkflow,
	PayoutSkipReason,
	PayoutStatus,
	type EarlyDisbursementOfferDto,
	type PaymentDto,
	type PaymentFailureCode,
	type PayoutDetailDto,
	type PayoutRequestListItemDto,
	type PayoutRetriedPayload,
} from "@fp/admin-api-client";
import { getPayoutRequestRoute } from "@/lib/payout-request-route";
import { Link } from "@tanstack/react-router";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { Button } from "@fp/ui/components/base/buttons/button";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";
import { useClipboard } from "@fp/ui/hooks/use-clipboard";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Copy01 } from "@untitledui/icons";
import { PayeeIcon } from "@/components/payee-icon";
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDataRefresh } from "@/hooks/useSignalR";
import { toast } from "@/lib/toast";
import { cx } from "@fp/ui/utils/cx";
import { useHotkeys } from "react-hotkeys-hook";
import { PayoutQueueNav } from "@/components/payout-queue-nav";
import {
	PayoutShortcutsHelp,
	PAYOUT_SHORTCUTS,
} from "@/components/payout-shortcuts-help";
import { usePayoutReviewEscape } from "@/hooks/usePayoutReviewEscape";
import { isOverlayOpen } from "@/lib/is-overlay-open";
import { usePayoutsQueue } from "@/lib/payouts-queue";

const VOIDABLE_STATUSES: string[] = [
	PayoutStatus.Scheduled,
	PayoutStatus.Released,
];

export const Route = createFileRoute("/payouts/$payoutId")({
	component: RouteComponent,
	loader: async ({ context: { queryClient }, params }) => {
		const payout = await queryClient.fetchQuery(
			getGetPayableByIdQueryOptions(params.payoutId),
		);
		return { payout: payout.data };
	},
	pendingComponent: () => <PageLoading />,
});

function PayoutDetailContent({ payout }: { payout: PayoutDetailDto }) {
	const [isRetryModalOpen, setIsRetryModalOpen] = useState(false);
	const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
	const [isPayNowModalOpen, setIsPayNowModalOpen] = useState(false);
	const [payoutsNotPaid, setPayoutsNotPaid] = useState<PayNowPayoutNotPaid[]>([]);
	const { data: capabilitiesData } = useGetOrganizationContextCapabilities();
	const { data: balanceData } = useGetApiAdminFundingBalance();
	const queryClient = useQueryClient();
	const router = useRouter();
	const payPayoutNowMutation = usePayPayoutNow();

	const latestPayment = payout.payments.at(-1);
	const currentFailure =
		payout.status !== PayoutStatus.Paid && payout.status !== PayoutStatus.Voided
			? [...payout.payments].reverse().find((payment) => payment.isFailed || payment.isReturned)
			: undefined;
	const showFailureDetails = Boolean(currentFailure);
	const showSkipDetails =
		Boolean(payout.skippedAt && payout.skipReason) &&
		(payout.status === PayoutStatus.Scheduled ||
			payout.status === PayoutStatus.Released);
	const canRetry = payout.canRetryManually;
	const canVoid =
		(capabilitiesData?.data?.canVoidPayout ?? false) &&
		VOIDABLE_STATUSES.includes(payout.status);
	const showVoidDetails = Boolean(payout.voidReason || payout.voidedAt);
	const canPayNow =
		(capabilitiesData?.data?.canPayPayoutsNow ?? false) &&
		canBePaidNow(payout, new Date());

	const openPayNow = () => {
		setPayoutsNotPaid([]);
		setIsPayNowModalOpen(true);
	};

	const payNow = async (reason: string) => {
		try {
			await payPayoutNowMutation.mutateAsync({ id: payout.id, data: { reason } });
			setIsPayNowModalOpen(false);
			await queryClient.invalidateQueries({ queryKey: getGetPayableByIdQueryOptions(payout.id).queryKey });
			await router.invalidate();
			toast.success("Payout paid now");
		} catch (error) {
			const notPaid = getPayoutsNotPaidNow(error);
			if (notPaid === null) {
				toast.error("Couldn't pay this payout now", { description: getErrorMessage(error, "Failed to pay the payout now") });
				return;
			}
			setPayoutsNotPaid(notPaid.map((notPaidPayout) => ({ ...notPaidPayout, payeeName: payout.payeeName ?? "Unknown" })));
			await queryClient.invalidateQueries({ queryKey: getGetPayableByIdQueryOptions(payout.id).queryKey });
		}
	};
	const sourceLines = payout.source ? getPayoutSourceLabelLines(payout.source) : [];
	const hasMemo = getPayoutMemoTexts(payout).length > 0;
	const organizationTimeZone = useOrganizationTimeZone();
	const release = organizationTimeZone ? formatRelease(payout, organizationTimeZone) : null;
	const earlyDisbursement = payout.earlyDisbursement;
	const election = earlyDisbursement?.election;
	const isElected = payout.disbursementTiming === DisbursementTiming.Early && Boolean(election);
	const showOffer = !isElected && payout.status === PayoutStatus.Scheduled;

	return (
		<>
			<div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
				<div className="px-4 py-4">
					<div className="flex items-center gap-2 text-base font-semibold text-brand-600">
						<PayeeIcon payee={payout.payee} className="h-4 w-4 shrink-0" />
						{payout.payeeName ?? "Unknown"}
					</div>
					{(sourceLines.length > 0 || hasMemo) && (
						<div className="mt-1">
							{sourceLines.map((line) => (
								<div key={line} className="text-sm text-gray-700">
									{line}
								</div>
							))}
							<PayoutMemoLines payout={payout} />
						</div>
					)}
					{payout.source && (
						<div className="mt-2">
							<PayoutSourceTags source={payout.source} />
						</div>
					)}
				</div>

				<div className="px-4 py-4 flex items-center justify-between">
					<div>
						<div className="text-sm text-gray-500">Status</div>
						<div className="flex items-center gap-2 mt-1">
							<span
								className={cx(
									"inline-flex items-center px-2.5 py-0.5 rounded text-sm font-medium",
									getStatusBadgeStyles(payout),
								)}
							>
								{payout.status}
							</span>
							{payout.overdueSince && <OverdueTag />}
							{isElected && <EarlyDisbursementTag />}
						</div>
						{payout.overdueSince && (
							<div className="mt-1 text-sm text-warning-700">
								Overdue since {formatLocalDate(payout.overdueSince, "datetimeWithYear")}
							</div>
						)}
					</div>
					<div className="text-right">
						<div className="text-sm text-gray-500">Amount</div>
						<div className="text-2xl font-bold text-brand-600">
							{formatCurrency(payout.amount, payout.currency)}
						</div>
					</div>
				</div>

				{isElected && earlyDisbursement && election && (
					<div className="px-4 py-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-2">
							Early Disbursement
						</h3>
						<div className="grid grid-cols-2 gap-4">
							<DetailField label="Fee" value={`${formatPercent(earlyDisbursement.offer.feeRate)} · ${formatCurrency(earlyDisbursement.offer.feeAmount, payout.currency)}`} />
							<DetailField label="Amount owed" value={formatCurrency(payout.amountOwed, payout.currency)} />
							<DetailField label="Elected" value={formatLocalDate(election.electedAt, "datetimeWithYear")} />
							<DetailField label="Pays" value={organizationTimeZone && formatOrganizationDate(earlyDisbursement.offer.releaseDate, organizationTimeZone)} />
						</div>
					</div>
				)}

				{showOffer && earlyDisbursement && (
					<div className="px-4 py-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-2">
							Early Disbursement Offered
						</h3>
						<div className="text-sm text-gray-700">
							{organizationTimeZone && formatOffer(earlyDisbursement.offer, payout, organizationTimeZone)}
						</div>
					</div>
				)}

				<div className="px-4 py-4 grid grid-cols-2 gap-4">
					<DetailField label="Payout Method" value={latestPayment?.rail} />
					<DetailField label="Created" value={formatLocalDate(payout.createdDate, "datetimeWithYear")} />
					{release && <DetailField label="Releases" value={release} />}
					{payout.paidInFullDate && (
						<DetailField
							label="Paid"
							value={formatLocalDate(payout.paidInFullDate, "datetimeWithYear")}
						/>
					)}
					{payout.approvedDate && (
						<DetailField
							label="Approved"
							value={formatLocalDate(payout.approvedDate, "datetimeWithYear")}
						/>
					)}
				</div>

				{payout.payoutRequests.length > 1 && (
					<div className="px-4 py-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-2">
							Payout Requests
						</h3>
						<div className="space-y-2">
							{payout.payoutRequests.map((item) => (
								<PayoutRequestBreakdownRow
									key={item.payoutRequest.id}
									item={item}
									currency={payout.currency}
								/>
							))}
						</div>
					</div>
				)}

				{payout.status === PayoutStatus.Failed && (
					<div className="px-4 py-4">
						<ExpirationDetails failedAt={payout.modifiedDate} />
					</div>
				)}

				{showFailureDetails && currentFailure && (
					<div className="px-4 py-4">
						{isTerminalFailure(payout) ? (
							<PaymentFailureDetails
								failureCode={currentFailure.failureCode}
								failureCorrelationId={currentFailure.failureCorrelationId}
								returnCode={currentFailure.returnCode}
								payeeSuspensionReason={currentFailure.payeeSuspensionReason}
								payeeName={payout.payeeName}
								payeeType={payout.payee.type}
							/>
						) : (
							<AutomaticRetryDetails />
						)}
					</div>
				)}

				{showSkipDetails && payout.skippedAt && payout.skipReason && (
					<div className="px-4 py-4">
						<SkipDetails skippedAt={payout.skippedAt} skipReason={payout.skipReason} />
					</div>
				)}

				{payout.paidNowAt && (
					<div className="px-4 py-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-2">
							Released Early
						</h3>
						<div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
							<div className="text-sm text-gray-800">
								Released early by {payout.paidNowByName ?? "a staff member"} on {formatLocalDate(payout.paidNowAt, "datetimeWithYear")}
							</div>
							{payout.paidNowReason && (
								<div className="text-sm text-gray-700">{payout.paidNowReason}</div>
							)}
							{payout.paidNowFromReleaseDate && organizationTimeZone && (
								<div className="text-sm text-gray-500">
									Originally scheduled to release on {formatOrganizationDate(payout.paidNowFromReleaseDate, organizationTimeZone)}
								</div>
							)}
						</div>
					</div>
				)}

				{showVoidDetails && (
					<div className="px-4 py-4">
						<h3 className="text-sm font-semibold text-red-800 mb-2">
							Void Details
						</h3>
						<div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
							{payout.voidReason && (
								<div className="text-sm text-red-800">{payout.voidReason}</div>
							)}
							{payout.voidedAt && (
								<div className="text-sm text-red-600">
									Voided: {formatLocalDate(payout.voidedAt, "datetimeWithYear")}
								</div>
							)}
						</div>
					</div>
				)}

				{payout.payments.length > 0 && (
					<div className="px-4 py-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-2">
							Payments
						</h3>
						<div className="space-y-3">
							{[...payout.payments].reverse().map((payment, index) => (
								<PaymentAttemptRow
									key={index}
									payment={payment}
									currency={payout.currency}
									canViewProviderReferences={
										capabilitiesData?.data?.canViewProviderReferences ?? false
									}
								/>
							))}
						</div>
					</div>
				)}
			</div>

			{(canPayNow || canVoid) && (
				<div className="mt-4 space-y-2">
					{canPayNow && (
						<Button
							color="primary"
							className="w-full"
							onClick={openPayNow}
						>
							Pay Now
						</Button>
					)}
					{canVoid && (
						<Button
							color="secondary-destructive"
							className="w-full"
							onClick={() => setIsVoidModalOpen(true)}
						>
							Void Payout
						</Button>
					)}
				</div>
			)}

			{canRetry && (
				<div className="mt-4">
					<Button
						color="primary"
						className="w-full"
						onClick={() => setIsRetryModalOpen(true)}
					>
						Retry Payout
					</Button>
				</div>
			)}

			<RetryPayoutModal
				isOpen={isRetryModalOpen}
				onClose={() => setIsRetryModalOpen(false)}
				payoutId={payout.id}
			/>

			<VoidPayoutModal
				isOpen={isVoidModalOpen}
				onClose={() => setIsVoidModalOpen(false)}
				payoutId={payout.id}
			/>

			<PayNowModal
				isOpen={isPayNowModalOpen}
				onOpenChange={setIsPayNowModalOpen}
				payouts={[payout]}
				availableBalance={balanceData?.data?.availableBalance ?? 0}
				isSubmitting={payPayoutNowMutation.isPending}
				payoutsNotPaid={payoutsNotPaid}
				onConfirm={payNow}
			/>
		</>
	);
}

function getAttemptTypeLabel(payment: PaymentDto): string | undefined {
	switch (payment.attemptType) {
		case "AutomaticRetry":
			return "Automatic retry";
		case "ManualRetry":
			return payment.retriedByName
				? `Manual retry by ${payment.retriedByName}`
				: "Manual retry";
		default:
			return undefined;
	}
}

function PayoutRequestBreakdownRow({
	item,
	currency,
}: {
	item: PayoutRequestListItemDto;
	currency: string;
}) {
	const sourceLines = getSourceLabelLines(item.source);
	const route = getPayoutRequestRoute(item.payoutRequest.id, item.source);

	const content = (
		<>
			<div className="min-w-0">
				{sourceLines.length > 0 ? (
					sourceLines.map((line) => (
						<div key={line} className="text-sm text-gray-700 truncate">
							{line}
						</div>
					))
				) : (
					<div className="text-sm text-gray-700">{item.source.workflow === PayoutRequestWorkflow.StaffMemberGenerated ? "Direct payment" : "Payout request"}</div>
				)}
				<div className="text-xs text-gray-500 mt-1">
					Submitted {formatLocalDate(item.payoutRequest.createdDate, "datetimeWithYear")}
				</div>
			</div>
			<div className="text-sm font-semibold text-gray-900 shrink-0">
				{formatCurrency(item.payoutRequest.amount ?? 0, currency)}
			</div>
		</>
	);

	return (
		<Link
			to={route.to}
			params={route.params}
			className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
		>
			{content}
		</Link>
	);
}

function PaymentAttemptRow({
	payment,
	currency,
	canViewProviderReferences,
}: {
	payment: PaymentDto;
	currency: string;
	canViewProviderReferences: boolean;
}) {
	const { copy } = useClipboard();
	const attemptDate =
		payment.returnedDate ?? payment.settledDate ?? payment.initiatedDate ?? payment.createdDate;
	const attemptTypeLabel = getAttemptTypeLabel(payment);

	const handleCopyReference = async () => {
		const { success } = await copy(payment.reference);

		if (success) {
			toast.success("Reference copied");
		} else {
			toast.error("Failed to copy the reference");
		}
	};

	return (
		<div className="text-sm">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<span
						className={cx(
							"inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
							payment.isFailed || payment.isReturned
								? "bg-red-100 text-red-800"
								: payment.status === "Settled"
									? "bg-green-100 text-green-800"
									: "bg-blue-100 text-blue-800",
						)}
					>
						{payment.status}
					</span>
					<span className="text-gray-700">
						{formatCurrency(payment.allocatedAmount, currency)}
					</span>
					{payment.payoutCount > 1 && (
						<span className="text-gray-500">
							of {formatCurrency(payment.amount, currency)} across {payment.payoutCount} payouts
						</span>
					)}
					<span className="text-gray-500">{payment.rail}</span>
				</div>
				<div className="text-gray-500">
					{formatLocalDate(attemptDate, "datetimeWithYear")}
				</div>
			</div>
			{(attemptTypeLabel || canViewProviderReferences) && (
				<div className="flex items-center justify-between gap-3 mt-0.5">
					<div className="text-xs text-gray-500 shrink-0">
						{attemptTypeLabel}
					</div>
					{canViewProviderReferences && (
						<button
							type="button"
							className="flex items-center gap-1 text-xs text-gray-400 font-mono hover:text-gray-600 min-w-0"
							title="Copy the Astra reference"
							onClick={handleCopyReference}
						>
							<span className="truncate">Ref: {payment.reference}</span>
							<Copy01 className="h-3.5 w-3.5 shrink-0" />
						</button>
					)}
				</div>
			)}
		</div>
	);
}

function AutomaticRetryDetails() {
	return (
		<>
			<h3 className="text-sm font-semibold text-amber-800 mb-2">
				Payment attempt failed
			</h3>
			<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
				<div className="text-sm text-amber-800">
					This attempt didn't go through. FieldPay will keep trying
					automatically.
				</div>
			</div>
		</>
	);
}

function ExpirationDetails({ failedAt }: { failedAt: string }) {
	return (
		<>
			<h3 className="text-sm font-semibold text-red-800 mb-2">Failed after staying overdue</h3>
			<div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
				<div className="text-sm text-red-700">
					No disbursement run could pay this payout for the whole expiration window. FieldPay stopped trying. Fix what held it up, then retry it.
				</div>
				<div className="text-sm text-red-600">
					Failed {formatLocalDate(failedAt, "datetimeWithYear")}
				</div>
			</div>
		</>
	);
}

const SKIP_COPY: Record<PayoutSkipReason, { title: string; body: string }> = {
	[PayoutSkipReason.InsufficientFunds]: {
		title: "Skipped because of insufficient funds",
		body: "The organization's cleared balance didn't cover this payout at the last disbursement run. FieldPay will pay it once funds are available.",
	},
	[PayoutSkipReason.PaymentUnconfirmed]: {
		title: "Skipped while a payment is being confirmed",
		body: "The payment provider didn't answer an earlier payment to this payee. FieldPay is confirming whether that payment went through and will pay this payout once it knows.",
	},
	[PayoutSkipReason.AwaitingRetry]: {
		title: "Skipped while waiting to retry",
		body: "The payment provider couldn't take an earlier payment to this payee. FieldPay waits a while after each failed attempt and will include this payout in the next one.",
	},
	[PayoutSkipReason.CannotBePaid]: {
		title: "Skipped because it can't be paid yet",
		body: "At the last disbursement run the payee couldn't receive a payment or the organization couldn't send one. FieldPay will include this payout in the first run after that is put right.",
	},
};

function SkipDetails({
	skippedAt,
	skipReason,
}: {
	skippedAt: string;
	skipReason: PayoutSkipReason;
}) {
	const { title, body } = SKIP_COPY[skipReason];

	return (
		<>
			<h3 className="text-sm font-semibold text-amber-800 mb-2">{title}</h3>
			<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
				<div className="text-sm text-amber-800">{body}</div>
				<div className="text-sm text-amber-600">
					Last run {formatLocalDate(skippedAt, "datetimeWithYear")}
				</div>
			</div>
		</>
	);
}

function PaymentFailureDetails({
	failureCode,
	failureCorrelationId,
	returnCode,
	payeeSuspensionReason,
	payeeName,
	payeeType,
}: {
	failureCode?: PaymentFailureCode | null;
	failureCorrelationId?: string | null;
	returnCode?: string | null;
	payeeSuspensionReason?: string | null;
	payeeName?: string | null;
	payeeType: PartyType;
}) {
	const { copied, copy } = useClipboard();
	const { title, body } = getPaymentFailureCopy(failureCode, payeeName, failureCorrelationId, payeeType);

	const handleCopy = async () => {
		if (!failureCorrelationId) {
			return;
		}

		const { success } = await copy(failureCorrelationId);

		if (success) {
			toast.success("Correlation ID copied");
		} else {
			toast.error("Failed to copy the correlation ID");
		}
	};

	return (
		<>
			<h3 className="text-sm font-semibold text-red-800 mb-2">{title}</h3>
			<div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
				<div className="text-sm text-red-700">{body}</div>
				{returnCode && (
					<div className="text-xs text-red-600 font-mono">Bank return code: {returnCode}</div>
				)}
				{payeeSuspensionReason && (
					<div className="text-sm text-red-700">Reason given by the payment processor: {payeeSuspensionReason}</div>
				)}
				{failureCorrelationId && (
					<div className="flex items-center justify-between gap-3">
						<div className="text-xs text-red-600 font-mono break-all">
							Correlation ID: {failureCorrelationId}
						</div>
						<Button size="sm" color="secondary" onClick={handleCopy}>
							{copied ? "Copied" : "Copy"}
						</Button>
					</div>
				)}
			</div>
		</>
	);
}

function formatOffer(offer: EarlyDisbursementOfferDto, payout: PayoutDetailDto, timeZoneId: string): string {
	const paysNow = new Date(offer.releaseDate).getTime() <= new Date(payout.createdDate).getTime();
	const when = paysNow ? "now" : `on ${formatOrganizationDate(offer.releaseDate, timeZoneId)}`;
	const terms = offer.feeAmount > 0
		? `for a ${formatPercent(offer.feeRate)} fee (${formatCurrency(offer.feeAmount, payout.currency)}) · ${formatCurrency(payout.amount - offer.feeAmount, payout.currency)} owed`
		: "with no fee";

	return `Pay ${when} ${terms}`;
}

function DetailField({
	label,
	value,
}: {
	label: string;
	value?: string | null;
}) {
	if (!value) return null;
	return (
		<div>
			<div className="text-sm text-gray-500">{label}</div>
			<div className="text-sm font-medium text-gray-900">{value}</div>
		</div>
	);
}

function RouteComponent() {
	const { payoutId } = Route.useParams();
	const { payout: loaderPayout } = Route.useLoaderData();
	const { data: queryData } = useGetPayableById(payoutId);
	const queryClient = useQueryClient();
	const router = useRouter();

	const invalidatePayout = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: getGetPayableByIdQueryOptions(payoutId).queryKey });
		router.invalidate();
	}, [queryClient, payoutId, router]);
	useDataRefresh(DataRefreshEvent.PayoutRequestPaid, invalidatePayout);
	useDataRefresh(DataRefreshEvent.PayoutReleased, invalidatePayout);
	useDataRefresh(DataRefreshEvent.PayoutOverdue, invalidatePayout);
	useDataRefresh(DataRefreshEvent.PayoutTerminallyFailed, invalidatePayout);
	useDataRefresh(DataRefreshEvent.PayoutVoided, invalidatePayout);
	useDataRefresh<PayoutRetriedPayload>(DataRefreshEvent.PayoutRetried, (payload) => {
		invalidatePayout();
		if (payload.payoutId === payoutId) {
			toast.success("Payout retried");
		}
	});

	const { goToNext, goToPrevious, returnToList, hasPrevious, hasNext } =
		usePayoutsQueue(payoutId);

	useHotkeys(
		["j", "down"],
		() => {
			if (!isOverlayOpen()) {
				goToNext();
			}
		},
		[goToNext],
	);
	useHotkeys(
		["k", "up"],
		() => {
			if (!isOverlayOpen()) {
				goToPrevious();
			}
		},
		[goToPrevious],
	);
	usePayoutReviewEscape(returnToList);

	const payout = queryData?.data ?? loaderPayout;

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Payout Details"
				parent={{ url: "/payout-requests", label: "Pay" }}
				titleActions={
					<>
						<PayoutQueueNav
							onPrevious={goToPrevious}
							onNext={goToNext}
							hasPrevious={hasPrevious}
							hasNext={hasNext}
						/>
						<PayoutShortcutsHelp shortcuts={PAYOUT_SHORTCUTS} />
					</>
				}
			/>
			<PageContentContainer>
				{payout ? <PayoutDetailContent payout={payout} /> : <PageLoading />}
			</PageContentContainer>
		</AdminLayout>
	);
}
